import { APIErrorResponse, isAPIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import { dispatchLogoutEvent } from '../sdk/logout';
import Identity from './identity';

import { headers, logPrefix } from './constants';

/**
 * Puts a user record in the to be deleted queue for a CSR user to approve and delete the user's account
 *
 * @returns
 * A _Promise_ resolving to an empty object if successful
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#
 * ```
 * </details>
 *
 */
export default async function requestDeleteAccount(): Promise<object | APIErrorResponse> {
  const isLoggedIn = await Identity.isLoggedIn();
  if (!isLoggedIn) {
    throw new Error(`${logPrefix} Missing or invalid jwt`);
  }

  return fetch(`${Identity.apiOrigin}/identity/public/v1/user/anonymize`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
      ...headers
    }
  })
    .then(JSONResponseHandler)
    .then(json => {
      if (isAPIErrorResponse(json)) {
        throw json;
      } else {
        return json;
      }
    });
}

/**
 * Approves the previously requested account deletion
 *
 * @param nonce
 *
 * @returns
 * A _Promise_ resolving to an empty object if successful
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#
 * ```
 * </details>
 *
 */
export function approveDeleteAccount(nonce: string): Promise<object | APIErrorResponse> {
  return Identity.heartbeat()
    .then(() =>
      fetch(`${Identity.apiOrigin}/identity/public/v1/user/anonymize/approve/${nonce}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
          ...headers
        },
        body: JSON.stringify({})
      })
    )
    .then(JSONResponseHandler)
    .then(json => {
      if (isAPIErrorResponse(json)) {
        throw json;
      } else {
        Identity.clearSession();
        dispatchLogoutEvent();
        return json;
      }
    });
}

export type DeclineDeleteReason =
  | 'MISTAKE'
  | 'CHANGED_MIND'
  | 'OTHER'
  | 'DID_NOT_REQUEST_DELETION ';

/**
 * Delines the previously requested account deletion
 *
 * @param nonce
 * @param reason
 *
 * @returns
 * A _Promise_ resolving to an empty object if successful
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#
 * ```
 * </details>
 *
 */
export async function declineDeleteAccount(
  nonce: string,
  reason: DeclineDeleteReason
): Promise<object | APIErrorResponse> {
  const isLoggedIn = await Identity.isLoggedIn();
  if (!isLoggedIn) {
    throw new Error(`${logPrefix} Missing or invalid jwt`);
  }

  return fetch(`${Identity.apiOrigin}/identity/public/v1/user/anonymize/decline/${nonce}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
      ...headers
    },
    body: JSON.stringify({ reason })
  })
    .then(JSONResponseHandler)
    .then(json => {
      if (isAPIErrorResponse(json)) {
        throw json;
      } else {
        return json;
      }
    });
}
